import { useState, useEffect } from 'react'
import { AccountCredentialForm } from '@/components/integrations/AccountCredentialForm'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { toast } from 'sonner'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import * as z from 'zod'

const propertyMeldAdditionalSchema = z.object({
  defaultProperty: z.string().optional(),
})

export interface PropertyMeldConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function PropertyMeldConnectForm({
  isEditMode = false,
  onConnected,
}: PropertyMeldConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    accountIdentifier: string
  } | null>(null)
  const [defaultProperty, setDefaultProperty] = useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const propertyMeld = integrations.find(
          i =>
            i.type === INTEGRATIONS.PROPERTY_MELD.TYPE ||
            i.name === INTEGRATIONS.PROPERTY_MELD.NAME,
        )
        if (propertyMeld?.connected && propertyMeld.accountId) {
          setExistingCredential({ accountIdentifier: propertyMeld.accountId })
          if (propertyMeld.metadata?.defaultProperty) {
            setDefaultProperty(propertyMeld.metadata.defaultProperty as string)
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    accountIdentifier: string
    password: string
    defaultProperty?: string
  }) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    const metadata = data.defaultProperty
      ? { defaultProperty: data.defaultProperty }
      : undefined

    try {
      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.propertyMeld.updateAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.propertyMeld.updateCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Property Meld credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.propertyMeld.createAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.propertyMeld.createCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Property Meld integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} PropertyMeld:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} PropertyMeld integration`,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <AccountCredentialForm
      onSubmit={handleSubmit}
      accountIdentifierLabel="Email"
      accountIdentifierPlaceholder="your-email@example.com"
      passwordLabel="Password"
      passwordPlaceholder="Enter your password"
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      initialAccountIdentifier={existingCredential?.accountIdentifier}
      additionalSchema={propertyMeldAdditionalSchema}
      initialValues={{ defaultProperty }}
      confirmationText={
        isEditMode
          ? 'Update your credentials for Property Meld'
          : 'Connect your credentials for Property Meld'
      }
    >
      {({ form, isSubmitting }) => (
        <FormField
          control={form.control}
          name="defaultProperty"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>Default property (optional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="121 Main St, Anytown, CA"
                  disabled={isSubmitting}
                  value={field.value as string}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              </FormControl>
              <FormDescription>
                The default property address to use for the integration
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </AccountCredentialForm>
  )
}
